import React, { useRef, useEffect, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { arrayOf, shape, string } from 'prop-types'
import {
  HeaderBlock,
  Grid,
  Cell,
  Card,
  DialogOverlay,
  DialogOverlayContent,
  Divider,
  useOverlay,
  List,
  ListItem,
  CollapsableListItem,
  useAtSize,
  Spinner,
  Button,
  useStatusOverlay,
  AudioRecorder,
  AudioPlayer,
  TextField,
} from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'
import { css } from '@emotion/core'

import { trackEvent } from '@fs/zion-analytics'
import { ContentAdd, NoticeLoading } from '@fs/zion-icon'
import DSRenderWithMode from '../../../../../helpers/DSRenderWithMode'
import audioRecorderReducer from '../../../../AudioRecorder/AudioRecorderReducer'
import filterMemories from '../../../../../helpers/memory_helper'
import { usePostAudioMemories, useDeleteMemory } from '../../../hooks/postHooks'
import useMemories from '../../../../../hooks/useMemories'
import handleFileUploadAttempt from '../../../../../helpers/upload'
import DeleteMemoryButton from '../../../../DeleteMemoryButton/DeleteMemoryButton'
import { usePersonViewerState } from '../../../state/PersonViewerStore'

const formCSS = css`
  display: none;
`
const audioPlayerCSS = css`
  width: 100%;
`
const gridContainerCSS = css`
  margin: 20px;
`

const audioTitleCSS = css`
  font-weight: bold;
  padding: 10px;
`

const cssAddMemory = css`
  border: 4px dotted #e0e0e0;
  margin: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  color: #999;
  vertical-align: top;
  width: 200px;
  height: 200px;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

const spinnerContainer = css`
  margin: auto 0;
`

export default function PersonAudio({ personId, gender, centerOrStation }) {
  const [t] = useTranslation()
  const user = useUser()
  const { signedIn } = user
  const memoryType = 'audio'
  const [isLoading, setIsLoading] = React.useState(true)
  const [audioList, setAudioList] = React.useState([])
  const uploadForm = useRef(null)
  const uploadTrigger = useRef(null)
  const atSize = useAtSize()
  const audioOverlay = useOverlay()
  const showStatusOverlay = useStatusOverlay()
  const [memoriesState, dispatch] = useMemories({ personId })
  const [showRecorder, setShowRecorder] = useState(true)
  const [audioSource, setAudioSource] = useState('')
  const [showSubmitBtn, setShowSubmitBtn] = useState(false)
  const [uploadPending, setUploadPending] = useState(false)
  const [showRecordResponseLabel, setShowRecordResponseLabel] = React.useState(true)
  const [newAudio, postNewAudio] = usePostAudioMemories()
  const [oldMemory, deleteMemory] = useDeleteMemory()
  const [disableSave, setDisableSave] = useState(false)
  const addAudioTitle = t('person-details.add-audio', 'Add audio')
  const personViewerState = usePersonViewerState()
  const { lockMemories } = personViewerState

  const [recordingState, recordingDispatcher] = React.useReducer(audioRecorderReducer, {
    status: 'STOPPED',
    length: 0,
    recordedAudio: null,
    response: null,
    title: '',
  })

  const { isOpen, transitioning } = audioOverlay
  useEffect(() => {
    if (!isOpen && transitioning) {
      recordingDispatcher({ type: 'RESETTING' })
      setShowRecordResponseLabel(true)
      setShowRecorder(false)
      setAudioSource('')
    }
  }, [isOpen, transitioning])

  React.useEffect(() => {
    if (showRecordResponseLabel) {
      const hasContent = recordingState && recordingState.length > 0
      setShowRecordResponseLabel(!hasContent)
    }
  }, [recordingState, showRecordResponseLabel])

  React.useEffect(() => {
    if (!isOpen && transitioning) {
      recordingDispatcher({ type: 'RESETTING' })
      setShowRecordResponseLabel(true)
      setShowRecorder(false)
      setAudioSource('')
      setDisableSave(false)
    }
  }, [isOpen, transitioning])

  React.useEffect(() => {
    if (recordingState.status === 'CLOSE_DIALOG') {
      audioOverlay.close()
      setDisableSave(false)
      recordingDispatcher({ type: 'WAITING' })
    }
  }, [recordingState, audioOverlay, recordingDispatcher])

  React.useEffect(() => {
    if (recordingState.status === 'OPEN_AUDIO_DIALOG') {
      audioOverlay.open()
      setShowRecorder(true)
      recordingDispatcher({ type: 'WAITING' })
    }
  }, [recordingState, audioOverlay, recordingDispatcher])

  const handleRecordingDone = (audio) => {
    setShowRecorder(false)
    setShowSubmitBtn(true)
    setAudioSource(audio)
  }

  const handleCancel = () => {
    audioOverlay.close()
    setDisableSave(false)
    setAudioSource('')
    setShowRecorder(true)
  }

  React.useEffect(() => {
    if (newAudio.pending) {
      setUploadPending(true)
      showStatusOverlay({
        message: t(`person-details.uploading-memory-${memoryType}`),
        type: 'normal',
        dismissable: true,
      })
    }
    setUploadPending(false)
  }, [newAudio.pending, showStatusOverlay, t])

  React.useEffect(() => {
    if (memoriesState.complete && memoriesState.res && memoriesState.res.sourceDescriptions) {
      const images = filterMemories({ memories: memoriesState.res, memoryType: 'audio' })
      setAudioList(images)
      setIsLoading(false)
    } else if (memoriesState.complete && memoriesState.res === '') {
      setIsLoading(false)
    }
  }, [memoriesState])

  React.useEffect(() => {
    if (newAudio.complete && !newAudio.error && !newAudio.pending) {
      showStatusOverlay({
        message: t(`person-details.upload-successful-${memoryType}`),
        type: 'success',
        dismissable: true,
        transient: true,
      })
      audioOverlay.close()
    }
  }, [audioOverlay, newAudio, showStatusOverlay, t])

  React.useEffect(() => {
    if (newAudio.complete && newAudio.error && !newAudio.pending) {
      showStatusOverlay({
        message: t(
          `person-details.status-${newAudio.error?.response?.status || 'error'}${
            newAudio.error?.response?.status === 409 ? `-${memoryType}` : ''
          }`
        ),
        type: 'error',
        transient: true,
        dismissable: true,
      })
    }
  }, [newAudio, showStatusOverlay, t])

  React.useEffect(() => {
    if (oldMemory.complete && !oldMemory.error && !oldMemory.pending) {
      dispatch({ type: 'load' })
    }
  }, [dispatch, oldMemory])

  const banner = css`
    width: 80%;
    background-color: #fff;
    text-align: center;
    margin: 0 auto;
    padding-bottom: 5px;
    margin-top: ${atSize({ default: '75px', xxl: '200px' })};
    button {
      margin-top: 20px;
    }
  `

  const btnRowCss = css`
    display: flex;
    width: 100%;
    margin: 20px 0;
    justify-content: center;
    position: relative;
    button:first-of-type {
      position: absolute;
      left: 0;
    }
  `
  const handleAudioSubmit = () => {
    const data = new FormData(uploadForm.current)
    postNewAudio({
      ancestorId: personId,
      data,
    })
  }

  const handleRecordingSubmit = () => {
    setDisableSave(true)
    const audioTitle = recordingState.title || t(`audio-recorder.record-story`, 'Record a story')
    const data = new FormData()
    data.append('audio', audioSource, `${audioTitle}.mp3`)
    postNewAudio({
      ancestorId: personId,
      data,
    })
  }

  const uploadAudio = () => {
    uploadTrigger.current.click()
    trackEvent({ event_name: 'Upload Audio' })
  }
  const handleRecordAudio = () => {
    recordingDispatcher({ type: 'OPEN_AUDIO_DIALOG' })
  }

  const handleAudioUploadOnChange = () => {
    handleFileUploadAttempt({
      fileForm: uploadForm.current,
      fileInputName: 'audio',
      uploadCallback: handleAudioSubmit,
      fileTooLargeHandler: () => {
        showStatusOverlay({
          message: t('person-details.file-too-large', 'The file is too large.  The size limit is 15 megabytes.'),
          type: 'error',
        })
      },
    })
  }

  return (
    <>
      {!isLoading && audioList.length > 0 && (
        <div>
          <div css={formCSS}>
            <form ref={uploadForm} id="audio-form" method="post" name="audioForm">
              <input
                ref={uploadTrigger}
                name="audio"
                onChange={handleAudioUploadOnChange}
                type="file"
                accept="audio/mpeg3"
                id="audio"
              />
            </form>
          </div>

          <div css={gridContainerCSS}>
            <Grid>
              {!lockMemories && !centerOrStation && (
                <DSRenderWithMode allowedModes={['WEB']}>
                  <Cell columns={atSize({ default: 12, sm: 6, lg: 4, xl: 3, xxl: 2 })} key="add-button-cell">
                    <div css={cssAddMemory}>
                      <ContentAdd size="xl" color="#aaa" />
                      <List>
                        <CollapsableListItem primaryText="Add Audio">
                          <ListItem
                            onClick={uploadAudio}
                            primaryText={t('person-details.upload-audio-file', 'Upload audio file')}
                          />
                          <ListItem
                            onClick={handleRecordAudio}
                            primaryText={t('person-details.record-audio', 'Record audio')}
                          />
                        </CollapsableListItem>
                      </List>
                    </div>
                  </Cell>
                </DSRenderWithMode>
              )}
              {audioList.map((memory) => (
                <Cell columns={atSize({ default: 12, sm: 6, lg: 4, xl: 3, xxl: 2 })} key={memory.link}>
                  <Card>
                    <Card.Body>
                      <div css={audioTitleCSS}>{memory.title}</div>
                      <audio css={audioPlayerCSS} key={memory.link} controls src={memory.link}>
                        <track default="" kind="captions" />
                      </audio>
                    </Card.Body>
                    {memory.resourceId === user.cisId && !lockMemories && !centerOrStation && (
                      <Card.Actions>
                        <DeleteMemoryButton artifactId={memory.id} deleteMemory={deleteMemory} />
                      </Card.Actions>
                    )}
                  </Card>
                </Cell>
              ))}
              {(newAudio.pending || oldMemory.pending) && (
                <Cell columns={atSize({ default: 12, sm: 6, lg: 4, xl: 3, xxl: 2 })}>
                  <div css={spinnerContainer}>
                    <Spinner large />
                  </div>
                </Cell>
              )}
            </Grid>
          </div>
        </div>
      )}

      {!isLoading && audioList.length === 0 && (
        <div css={banner}>
          {lockMemories && <HeaderBlock size="md" heading={t('person-details.no-records', 'No records found')} />}
          {centerOrStation && (
            <HeaderBlock
              size="md"
              heading={t('person-details.be-first-center', 'Be the first to add to this record on FamilySearch.org')}
            />
          )}
          {!lockMemories && !centerOrStation && (
            <div>
              <div css={formCSS}>
                <form ref={uploadForm} id="audio-form" method="post" name="audioForm">
                  <input
                    ref={uploadTrigger}
                    name="audio"
                    onChange={handleAudioUploadOnChange}
                    type="file"
                    accept="audio/mpeg3"
                    id="audio"
                  />
                </form>
              </div>
              <div>
                <HeaderBlock size="md" heading={t('person-details.be-first', 'Be the first to add to this record')} />
                <div>
                  <Button
                    emphasis="billboard"
                    color={gender && gender === 'male' ? 'blue' : 'red'}
                    onClick={uploadAudio}
                  >
                    {t('person-details.upload-audio-file', 'Upload audio file')}
                  </Button>
                </div>
                <div>
                  <Button
                    emphasis="billboard"
                    color={gender && gender === 'male' ? 'blue' : 'red'}
                    onClick={handleRecordAudio}
                  >
                    {t('person-details.record-audio', 'Record audio')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {isLoading && audioList.length === 0 && (
        <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '500px' }}>
          <Spinner large />
        </div>
      )}
      <DialogOverlay {...audioOverlay} headingText={addAudioTitle}>
        <DialogOverlayContent>
          <TextField
            value={recordingState.title}
            label={t(`audio-recorder.enter-title`, 'Enter a title')}
            maxLength={150}
            onChange={(e) =>
              recordingDispatcher({
                type: 'SET_TITLE',
                payload: e.target.value,
              })
            }
          />{' '}
          <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {showRecorder && <AudioRecorder onCancel={handleCancel} onFinish={handleRecordingDone} />}
            {audioSource && <AudioPlayer source={window.URL.createObjectURL(audioSource)} />}
            {showSubmitBtn && audioSource && !uploadPending && (
              <div css={btnRowCss}>
                <Button emphasis="low" onClick={handleCancel}>
                  {t('audio-recorder.cancel', 'Cancel')}
                </Button>
                <Button emphasis="high" onClick={handleRecordingSubmit} disabled={disableSave}>
                  {t('audio-recorder.save', 'Save')}
                </Button>
              </div>
            )}
            {uploadPending && (
              <div css={{ margin: '20px 0' }}>
                <NoticeLoading size="md" />
              </div>
            )}
          </div>
          {showRecordResponseLabel ? (
            <>
              <Divider mode="middle" />
              {showSubmitBtn && audioSource && !uploadPending ? (
                <div css={{ margin: '20px 0' }}>
                  <Trans id18nKey="audio-recorder.legal">
                    FamilySearch is a family-oriented site and is intended to be appropriate for all ages. All added
                    memories are public. Review the FamilySearch{' '}
                    <a
                      href="https://www.familysearch.org/legal/familysearch-content-submission-agreement"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Submission Agreement
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://www.familysearch.org/legal/familysearch-upload-guidelines-and-policies"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Upload Guidelines and Policies
                    </a>
                  </Trans>
                </div>
              ) : (
                signedIn && <></>
              )}
            </>
          ) : null}
        </DialogOverlayContent>
      </DialogOverlay>
    </>
  )
}

PersonAudio.propTypes = {
  personId: string,
  audioList: arrayOf(shape({ title: string, link: string })),
}
